import { useImperativeHandle } from 'react';
import { useUpdateEffect } from 'react-use';

import { VisitTemplate } from '@dreryk/egabinet-visit-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { Form, FormContent, Scroll } from '@dreryk/react-components';

import { TemplatesFormProps } from '../../shared/types';

import { VisitTemplateFormInterviewTextarea } from './fields/VisitTemplateFormInterview';
import { VisitTemplateFormPhysicalExaminationTextarea } from './fields/VisitTemplateFormPhysicalExaminationTextarea';
import { VisitTemplateFormRecommendationsTextarea } from './fields/VisitTemplateFormRecommendationsTextarea';
import { VisitTemplateFormBean, VisitTemplateFormController } from './VisitTemplateForm.types';
import { mapVisitTemplateToFormTemplate } from './VisitTemplateForm.utils';
import { VisitTemplateFormTemplateNameTextInput } from './fields/VisitTemplateFormTemplateNameTextInput';

export type VisitTemplateFormChildrenRenderProps = {
   handleSubmit: VisitTemplateFormController['handleSubmit'];
   formState: VisitTemplateFormController['formState'];
   templateEmpty: boolean;
};

type VisitTemplateFormProps = TemplatesFormProps<VisitTemplate, VisitTemplateFormChildrenRenderProps>;

export const VisitTemplateForm = (props: VisitTemplateFormProps): JSX.Element => {
   const { register, reset, formState, handleSubmit, watch, control } = useFormController<VisitTemplateFormBean>(
      props.template ? mapVisitTemplateToFormTemplate(props.template) : {}
   );
   const {
      fieldsRef: { current: formFieldsRef },
   } = control;

   const currentValues = watch();

   const templateEmpty =
      !currentValues.recommendations && !currentValues.interview && !currentValues.subjectiveAssessment;

   useImperativeHandle(
      props.templatesApiRef,
      () => ({
         onActiveItemClick: () => {
            formFieldsRef.drugName?.ref?.focus?.();
         },
      }),
      [formFieldsRef.drugName?.ref]
   );

   useUpdateEffect(() => {
      if (formFieldsRef.drugName) {
         formFieldsRef.drugName.ref.focus?.();
      }
   }, [formFieldsRef.drugName?.ref]);

   useUpdateEffect(() => {
      if (props.template) {
         reset(mapVisitTemplateToFormTemplate(props.template));
      }
   }, [props.template]);

   useUpdateEffect(() => {
      if (formState.isDirty) {
         props.onDirty();
         return;
      }
      props.onPristine();
   }, [formState.isDirty]);

   useUpdateEffect(() => {
      props.onTemplateNameChange(currentValues.name ?? '');
   }, [currentValues.name]);

   return (
      <Scroll testId={'visitTemplate'}>
         <Form noValidate>
            <FormContent testId={'content'}>
               <VisitTemplateFormTemplateNameTextInput template={props.template} register={register} />
               <VisitTemplateFormInterviewTextarea register={register} control={control} />
               <VisitTemplateFormPhysicalExaminationTextarea register={register} control={control} />
               <VisitTemplateFormRecommendationsTextarea register={register} control={control} />
            </FormContent>
            {props.children({ handleSubmit: handleSubmit, formState: formState, templateEmpty: templateEmpty })}
         </Form>
      </Scroll>
   );
};
